import { useState, useEffect } from 'react';
import { getASPAuditTrail } from '../api/auditApi';

export default function ASPActivityTimeline({ aspId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    loadTimeline();
  }, [aspId]);

  async function loadTimeline() {
    try {
      setLoading(true);
      setError(null);
      const result = await getASPAuditTrail(aspId);

      if (result.error) {
        setError(result.error);
      } else {
        setTimeline(result.events || []);
      }
    } catch (err) {
      console.error('Error loading timeline:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function getEventIcon(eventType) {
    switch (eventType) {
      case 'asp_created':
        return (
          <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'asp_shared_with_lawyer':
        return (
          <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
        );
      case 'asp_signed':
        return (
          <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'asp_fully_executed':
        return (
          <svg className="h-6 w-6 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'asp_status_changed':
        return (
          <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        );
      default:
        return (
          <svg className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  }

  function getEventTitle(event) {
    switch (event.event_type) {
      case 'asp_created':
        return 'Agreement Created';
      case 'asp_shared_with_lawyer':
        return `Shared with ${event.metadata?.lawyer_name || 'Lawyer'}`;
      case 'asp_signed':
        return `Signed by ${event.actor_type === 'buyer' ? 'Purchaser' : 'Vendor'}`;
      case 'asp_fully_executed':
        return 'Agreement Fully Executed';
      case 'asp_status_changed':
        return `Status: ${event.metadata?.new_status?.replace(/_/g, ' ')}`;
      case 'asp_document_generated':
        return 'Document Generated';
      default:
        return event.event_type.replace(/_/g, ' ');
    }
  }

  function getEventDescription(event) {
    switch (event.event_type) {
      case 'asp_created':
        return `Purchase price: $${event.metadata?.purchase_price?.toLocaleString()}`;
      case 'asp_shared_with_lawyer':
        return `Email: ${event.metadata?.lawyer_email}`;
      case 'asp_signed':
        return `Signature method: ${event.metadata?.signature_method}`;
      case 'asp_status_changed':
        return `Changed from "${event.metadata?.old_status?.replace(/_/g, ' ')}" to "${event.metadata?.new_status?.replace(/_/g, ' ')}"`;
      case 'asp_document_generated':
        return `Version ${event.metadata?.version} (${event.metadata?.document_type})`;
      default:
        return '';
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <p className="text-gray-500 text-center">No activity yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Timeline</h3>

      <div className="flow-root">
        <ul className="-mb-8">
          {timeline.map((event, idx) => (
            <li key={event.event_id}>
              <div className="relative pb-8">
                {idx !== timeline.length - 1 && (
                  <span
                    className="absolute top-6 left-3 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex space-x-3">
                  <div className="flex-shrink-0">
                    <span className="h-6 w-6 rounded-full flex items-center justify-center bg-white border-2 border-gray-200">
                      {getEventIcon(event.event_type)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {getEventTitle(event)}
                      </p>
                      {getEventDescription(event) && (
                        <p className="mt-0.5 text-sm text-gray-600">
                          {getEventDescription(event)}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        {new Date(event.timestamp).toLocaleString('en-NZ', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
